import { useField } from "formik"
import { DateTime } from "luxon"
import { FormikDateTimePicker, FormikTextField } from "../../../../../shared/components/forms/Formik"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { StandardMainPanel } from "../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../shared/components/forms/StandardSidePanel"
import { StandardForm } from "../../../../../shared/components/forms/StandardForm"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { CreateEditMessageFormValues, MessageAdditionalDataChannelsType } from "../messagesTypes"
import { StandardAlertInfo } from "../../../../../shared/components/alert/StandardAlertInfo"
import { MessageChannelsCards } from "./MessageChannelsCards"
import { ChannelCardConfig } from "./MessageChannelCard"
import { MessageTagAutoComplete } from "./MessageTagAutoComplete"
import { CreateUpdateMessageFormLocation, CreateUpdateMessageFormMode } from "./CreateUpdateMessageForm"
import { useDateTime } from "../../../../localization/useDateTime"
import { Fragment } from "react"
import { Typography, Accordion, AccordionDetails, AccordionSummary, Box } from "@mui/material"
import { Stack } from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { makeStyles } from "tss-react/mui"

export const TITLE_LIMIT = 50
export const MESSAGE_BODY_LIMIT = 170

const useStyles = makeStyles()((theme) => ({
    publishField: {
        maxWidth: "33%",
    },
    publishAccordion: {
        boxShadow: "none",
        "& .MuiAccordionSummary-root": {
            borderBottom: `1px solid ${theme.palette.tailwind.slate[400]}`,
            padding: 0,
        },
        "& .MuiAccordionSummary-expandIconWrapper": {
            color: theme.palette.tailwind.slate[400],
        },
        "&::before": {
            display: "none",
        },
        "&.Mui-expanded": {
            margin: 0,
        },
    },
    publishAccordionSummary: {
        minHeight: "auto",
        paddingLeft: 0,
        "&.Mui-expanded": {
            minHeight: "auto",
        },
        "& .MuiAccordionSummary-content": {
            margin: 0,
            padding: "0.5rem 0",
            "&.Mui-expanded": {
                margin: 0,
            },
        },
    },
    publishFieldContent: {
        padding: "1.875rem 0 0 0",
    },
}))

type CreateUpdateMessageFormContentProps = Readonly<{
    mode: CreateUpdateMessageFormMode
    location: CreateUpdateMessageFormLocation
    channelOptions?: ChannelCardConfig[]
}>

export const CreateUpdateMessageFormContent = ({
    mode,
    location,
    channelOptions,
}: CreateUpdateMessageFormContentProps) => {
    const { classes } = useStyles()
    const [{ value: body }] = useField<string>("body")
    const [{ value: channels }] = useField<MessageAdditionalDataChannelsType[]>("channels")
    const [{ value: expirationDateValue }] = useField<DateTime | null>("expirationDate")
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const isDialog = location === "dialog"
    const Wrapper = isDialog ? Fragment : StandardForm

    const messageLength = (body ?? "").length
    const hasInfoDisplaysChannel =
        (Array.isArray(channels) && channels.includes(MessageAdditionalDataChannelsType.INFO_DISPLAYS)) ||
        (mode === "edit" && expirationDateValue != null)

    const publishField = isDialog ? (
        <Stack gap="0.5rem">
            <LocalizedStrict id="create-or-edit-message-field-publish-title">
                <Typography variant="body1" fontWeight="500" color="text.primary">
                    Publish:
                </Typography>
            </LocalizedStrict>
            <Box className={classes.publishField}>
                <LocalizedStrict
                    id="create-or-edit-message-field-publishDate"
                    attrs={{ label: !isDialog, helperText: true }}
                >
                    <FormikDateTimePicker
                        name="publishTime"
                        minDate={dateTimeFactory.now()}
                        format={"MM/dd/yyyy\u00A0hh:mm\u00A0a"}
                        slotProps={{
                            textField: {
                                helperText: l10n.getString("create-or-edit-message-field-publishDate-helperText"),
                                fullWidth: true,
                            },
                        }}
                    />
                </LocalizedStrict>
            </Box>
        </Stack>
    ) : (
        <LocalizedStrict id="create-or-edit-message-field-publishDate-page" attrs={{ label: true, helperText: true }}>
            <FormikDateTimePicker
                name="publishTime"
                minDate={dateTimeFactory.now()}
                format={"MM/dd/yyyy\u00A0hh:mm\u00A0a"}
                slotProps={{
                    textField: {
                        helperText: l10n.getString("create-or-edit-message-field-publishDate-helperText"),
                        fullWidth: true,
                    },
                }}
            />
        </LocalizedStrict>
    )

    const expirationDateField = hasInfoDisplaysChannel ? (
        <Stack gap="0.75rem">
            <LocalizedStrict id="create-or-edit-message-field-expirationDate-info" attrs={{ title: true }}>
                <StandardAlertInfo title="The expiration only applies to info screen messages." />
            </LocalizedStrict>
            {isDialog ? (
                <Stack gap="0.5rem">
                    <LocalizedStrict id="create-or-edit-message-field-expirationDate-title">
                        <Typography variant="body1" fontWeight="500" color="text.primary">
                            Expiration:
                        </Typography>
                    </LocalizedStrict>
                    <Box className={classes.publishField}>
                        <LocalizedStrict
                            id="create-or-edit-message-field-expirationDate"
                            attrs={{ label: !isDialog, helperText: true }}
                        >
                            <FormikDateTimePicker
                                name="expirationDate"
                                minDate={dateTimeFactory.now()}
                                format={"MM/dd/yyyy\u00A0hh:mm\u00A0a"}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                    },
                                }}
                            />
                        </LocalizedStrict>
                    </Box>
                </Stack>
            ) : (
                <LocalizedStrict
                    id="create-or-edit-message-field-expirationDate-page"
                    attrs={{ label: true, helperText: true }}
                >
                    <FormikDateTimePicker
                        name="expirationDate"
                        minDate={dateTimeFactory.now()}
                        format={"MM/dd/yyyy\u00A0hh:mm\u00A0a"}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                            },
                        }}
                    />
                </LocalizedStrict>
            )}
        </Stack>
    ) : null

    const settings = isDialog ? (
        <Box>
            <Accordion defaultExpanded elevation={0} className={classes.publishAccordion}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.publishAccordionSummary}>
                    <LocalizedStrict id="create-or-edit-message-accordion-title">
                        <Typography variant="body1" fontWeight="700" color="text.primary">
                            Settings:
                        </Typography>
                    </LocalizedStrict>
                </AccordionSummary>
                <AccordionDetails className={classes.publishFieldContent}>
                    <Stack gap="1.5rem">
                        {publishField}
                        {expirationDateField}
                    </Stack>
                </AccordionDetails>
            </Accordion>
        </Box>
    ) : (
        <Stack gap="1.5rem">
            {publishField}
            {expirationDateField}
        </Stack>
    )

    return (
        <Wrapper>
            <StandardMainPanel hideBorder={isDialog} spacing={isDialog ? "1.5rem" : undefined}>
                {/* <LocalizedStrict id="create-or-edit-message-info" attrs={{ title: true }}>
                        <StandardAlertInfo title="Send short important messages to the users, eg. about closing days, events or similar. Messages will be visible for all users who are connected to your shops." />
                    </LocalizedStrict> */}

                {mode === "create" && (
                    <>
                        <MessageChannelsCards name="channels" channelOptions={channelOptions} />
                        <MessageTagAutoComplete name="tag" isDialog={isDialog} />
                    </>
                )}

                <Stack gap="0.5rem">
                    {isDialog && (
                        <LocalizedStrict id="create-or-edit-message-dialog-field-subject-label" attrs={{ label: true }}>
                            <Typography variant="body1" fontWeight="500" color="text.primary">
                                Subject:
                            </Typography>
                        </LocalizedStrict>
                    )}
                    <LocalizedStrict
                        id="create-or-edit-message-field-subject"
                        attrs={{ label: !isDialog, placeholder: isDialog, helperText: !isDialog }}
                    >
                        <FormikTextField<CreateEditMessageFormValues>
                            name="title"
                            label={isDialog ? undefined : "Subject"}
                            placeholder={isDialog ? "Add a catchy subject, which users wish to read *" : undefined}
                            helperText={isDialog ? undefined : "Add a catchy subject, which users wish to read."}
                            fullWidth
                            inputProps={{ maxLength: TITLE_LIMIT }}
                        />
                    </LocalizedStrict>
                </Stack>

                <Stack gap="0.5rem">
                    {isDialog && (
                        <LocalizedStrict id="create-or-edit-message-dialog-field-message-label" attrs={{ label: true }}>
                            <Typography variant="body1" fontWeight="500" color="text.primary">
                                Message:
                            </Typography>
                        </LocalizedStrict>
                    )}
                    <LocalizedStrict
                        id="create-or-edit-message-field-message"
                        attrs={{ label: !isDialog, placeholder: isDialog, helperText: true }}
                        vars={{ charsRemaining: MESSAGE_BODY_LIMIT - messageLength }}
                    >
                        <FormikTextField<CreateEditMessageFormValues>
                            name="body"
                            label={isDialog ? undefined : "Message"}
                            placeholder={isDialog ? "Enter a message *" : undefined}
                            fullWidth
                            multiline
                            maxRows={4}
                            inputProps={{ maxLength: MESSAGE_BODY_LIMIT }}
                            helperText={`Remaining characters: ${MESSAGE_BODY_LIMIT - messageLength}`}
                        />
                    </LocalizedStrict>
                </Stack>
                {isDialog && settings}
            </StandardMainPanel>
            {!isDialog && (
                <LocalizedStrict id="create-or-edit-message-settings" attrs={{ title: true }}>
                    <StandardSidePanel title="Settings">{settings}</StandardSidePanel>
                </LocalizedStrict>
            )}
        </Wrapper>
    )
}
